import {DimCollector, DimEntry, DimensionStack} from "../export/DimensionStack";
import {drawnOnDoorElev} from "../hardware/hardwarePreps";
import type {DoorElevView} from "./DoorElevScene";

// engineering-drawing dimensioning for the door export, around one leaf. Works in the
// leaf-face frame ([0,0] .. [leafW, leafH]) that the lites + hardware crosshairs are
// positioned in. Collects the overall leaf size, each lite (edge offsets + cut-out size),
// and each door-hardware hub position, then draws them stacked outside the leaf —
// horizontals across the top, verticals down the left. Replaces the inline lite/
// hardware dimensions.
export class ExportDoorDimensions {
    private view: DoorElevView;
    private stack: DimensionStack;

    constructor(view: DoorElevView) {
        this.view = view;
        this.stack = new DimensionStack(view.elev.draw);
    }

    draw() {
        const leafW = this.view.leaf.leafWidth();
        const leafH = this.view.leaf.leafHeight();
        if(leafW <= 0 || leafH <= 0) {
            this.stack.hide();
            return;
        }

        const horizontal = new DimCollector();
        const vertical = new DimCollector();

        // overall leaf size.
        horizontal.add(0, leafW, leafW);
        vertical.add(0, leafH, leafH);

        // each lite: edge offsets to the leaf edges plus its cut-out width/height. The
        // glass size is not dimensioned — the drawing gives the cut-out, and the glass
        // undersize belongs on the submittal's lite table.
        const leaf = this.view.active ? "active" : "inactive";
        for(const lite of this.view.elev.doorSpec.litesFor(leaf)) {
            const l = lite.offsetLeft.get();
            const t = lite.offsetTop.get();
            const w = lite.width.get();
            const h = lite.height.get();
            horizontal.add(0, l, l);
            horizontal.add(l, l + w, w);
            horizontal.add(l + w, leafW, leafW - (l + w));
            vertical.add(0, t, t);
            vertical.add(t, t + h, h);
            vertical.add(t + h, leafH, leafH - (t + h));
        }

        // each drawn prep hub: its offset from the left / top leaf edge. Match the
        // view's filter so only this leaf's drawn placements are located.
        const hardware = this.view.elev.hardwareGroup?.hardware ?? [];
        const left: DimEntry[] = [];
        const top: DimEntry[] = [];
        for(const p of this.view.elev.hardware.list) {
            if(!drawnOnDoorElev(p, this.view.active)) continue;
            const item = hardware.find(h => h.product === p.productId);
            if(!item) continue;

            const hubX = p.x.get();
            const hubY = p.y.get();
            if(hubX > 1) top.push({lo: 0, hi: hubX, value: hubX, lane: 0});
            if(hubY > 1) left.push({lo: 0, hi: hubY, value: hubY, lane: 0});
        }

        this.stack.draw({
            rect: {x: 0, y: 0, width: leafW, height: leafH},
            // the leaf redraws its own graphics but keeps its children, so re-attaching
            // each draw is safe and keeps the dims above the leaf face.
            parent: this.view.leaf.lines,
            top: [...horizontal.entries(), ...top],
            left: [...vertical.entries(), ...left],
            right: this.collectHinges(),
        });
    }

    // the hinge chain, drawn on the right (same method as the frame export): from the
    // top of the door (y = 0 in the leaf frame) down to the top of the first hinge,
    // then top-of-hinge to top-of-hinge for the rest. Each hinge's offsetTop is
    // measured from the leaf top, so its top edge is offsetTop − half the hinge height.
    private collectHinges(): DimEntry[] {
        const hinges = this.view.elev.hardware.hinges;
        if(hinges.list.length === 0) return [];

        const tops = hinges.list
            .map((h, i) => h.offsetTop.get() - hinges.hingeHeightAt(i) / 2)
            .sort((a, b) => a - b);

        const entries: DimEntry[] = [];
        let prev = 0; // top of the door
        for(const top of tops) {
            if(top - prev > 1) entries.push({lo: prev, hi: top, value: top - prev, lane: 0});
            prev = top;
        }
        return entries;
    }

    destroy() {
        this.stack.destroy();
    }
}
